/**
 * Beperkt een set aanwezigheidsrapporten tot de deelnemers die het scherm
 * daadwerkelijk toont, en rekent daar het aanwezigheidspercentage over uit.
 *
 * Waarom dit één functie is: het percentage bovenaan de kaart en de rijen in de
 * tabel moeten over dezelfde mensen gaan. Stond dit los, dan telt de kop de
 * rapporten van deelnemers die niet in de lijst staan — verwijderd, of buiten
 * het filter gevallen — en klopt het getal niet met wat eronder staat.
 *
 * De dashboardkaart en de live-report-widget rekenden dit allebei zelf uit en
 * waren het niet meer met elkaar eens: de kaart filterde altijd, de widget
 * alleen wanneer er een locatiefilter aanstond. Dezelfde maand gaf daardoor
 * twee percentages.
 *
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 * AI-CONTEXT: See docs/pitfalls.md#database-totaldocs-overcounts-respondents
 */

/**
 * @typedef {object} PresenceAttendanceStats
 * @property {Array<object>} reports - Alleen de rapporten van getoonde deelnemers
 * @property {number} percentage - Afgerond percentage niet-afwezige rapporten; 0
 *   als er niets te tellen valt
 */

/**
 * Afwezig = `Type: 'absent'`; elk ander Type (correct, correction, of geen)
 * telt als aanwezig. Eén definitie voor het percentage én de uitsplitsing.
 * @param {object} report
 * @returns {boolean}
 */
const isAbsentReport = (report) => report?.Type === 'absent'

/**
 * Ziek is geen eigen Type maar een afwezigheid met `Reason: 'ziek'` (keuzelijst
 * in DashboardPresenceCheckComponent; de reden is elders ook vrij te typen).
 * @param {object} report
 * @returns {boolean}
 */
const isSickReport = (report) =>
    isAbsentReport(report) && String(report?.Reason ?? '').trim().toLowerCase() === 'ziek'

/**
 * @param {Array<object>|null|undefined} reports - Rapporten uit `PersonaPresenceReports`
 * @param {Array<object>|null|undefined} personas - De deelnemers die het scherm toont
 * @returns {PresenceAttendanceStats}
 */
const presenceAttendanceStats = (reports, personas) => {
    const reportList = Array.isArray(reports) ? reports : []
    const personaList = Array.isArray(personas) ? personas : []

    const allowedIds = new Set(personaList.map((persona) => persona?.ID).filter(Boolean))
    const kept = reportList.filter((report) => allowedIds.has(report?.PersonaID))

    if (!kept.length) return { reports: kept, percentage: 0 }

    const present = kept.filter((report) => !isAbsentReport(report)).length

    return { reports: kept, percentage: Math.round((present / kept.length) * 100) }
}

/**
 * @typedef {object} PresenceBreakdown
 * @property {number} present - Aantal niet-afwezige regels
 * @property {number} absent - Afwezig om een andere reden dan ziek
 * @property {number} sick - Afwezig met reden 'ziek'
 * @property {number} total - Alle regels
 * @property {number|null} percentage - Afgerond aanwezigheidspercentage; `null`
 *   als er niets te tellen valt, zodat 'niets geregistreerd' geen 0% wordt
 *   (AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero)
 */

/**
 * Uitsplitsing voor de widget 'Aanwezigheid' van één deelnemer
 * (docs/features/livereport-per-deelnemer.md). Het percentage rekent zoals
 * `presenceAttendanceStats`, zodat organisatie- en deelnemerrapport het eens zijn.
 * @param {Array<object>|null|undefined} reports - Rapporten van één deelnemer
 * @returns {PresenceBreakdown}
 */
const presenceBreakdown = (reports) => {
    const reportList = Array.isArray(reports) ? reports : []
    const total = reportList.length
    const sick = reportList.filter(isSickReport).length
    const absentAll = reportList.filter(isAbsentReport).length
    const present = total - absentAll

    return {
        present,
        absent: absentAll - sick,
        sick,
        total,
        percentage: total ? Math.round((present / total) * 100) : null,
    }
}

export { presenceBreakdown }
export default presenceAttendanceStats
